/**
 * 🦊 **A rig's root bone, taken off the skin** — T288. Its own file so a test can
 * reach it: `creaturepaint.ts` imports the WebGPU renderer, which no test loads.
 */

import { Matrix4, Vector3 } from 'three';

/**
 * 🦊 **Take the skeleton's ROOT bone off every vertex** — T288.
 *
 * 🔴 **Found on a baked picture of the sleeping fox**: this rig's root bone sits
 * down by the front paws, and paw vertices were weighted to it. The root never
 * moves in any pose, so when the legs tucked away those vertices stayed behind
 * and drew a NEEDLE from the chest to the grass.
 *
 * ✅ **Two cases, measured with a probe on the posed body:**
 * - a vertex on the root AND other bones — its root share goes to the others,
 *   in proportion (407 of them on the fox);
 * - a vertex on the root ALONE — it is given wholly to the NEAREST other bone,
 *   measured in the rest pose. ⚠️ The first version left these alone, and the
 *   needle stayed.
 *
 * ⚠️ Called by `foxmesh.ts` AND `rigsheet.html`, so the picture is the game.
 * @returns how many vertices changed — for an F8 report.
 */
export function shedRootWeights(skinned: unknown): number {
  const mesh = skinned as {
    bindMatrix?: Matrix4;
    skeleton?: { bones: { parent: { isBone?: boolean } | null }[]; boneInverses: Matrix4[] };
    geometry: {
      attributes: Record<
        string,
        | {
            count: number;
            getX: (i: number) => number;
            getY: (i: number) => number;
            getZ: (i: number) => number;
            getComponent: (i: number, k: number) => number;
            setComponent: (i: number, k: number, v: number) => void;
            needsUpdate: boolean;
          }
        | undefined
      >;
    };
  };
  const skeleton = mesh.skeleton;
  const index = mesh.geometry.attributes.skinIndex;
  const weight = mesh.geometry.attributes.skinWeight;
  const position = mesh.geometry.attributes.position;
  if (
    skeleton === undefined ||
    index === undefined ||
    weight === undefined ||
    position === undefined
  )
    return 0;
  const roots = new Set<number>();
  skeleton.bones.forEach((b, i) => {
    if (b.parent?.isBone !== true) roots.add(i);
  });
  if (roots.size === 0) return 0;
  /* Every non-root bone's rest position, in the same space as a bound vertex. */
  const restAt = skeleton.boneInverses.map((inv, i) =>
    roots.has(i) ? null : new Vector3().setFromMatrixPosition(inv.clone().invert()),
  );
  const bind = mesh.bindMatrix ?? new Matrix4();
  const v = new Vector3();
  let changed = 0;
  for (let i = 0; i < index.count; i++) {
    let onRoot = 0;
    let rest = 0;
    for (let k = 0; k < 4; k++) {
      const w = weight.getComponent(i, k);
      if (roots.has(index.getComponent(i, k))) onRoot += w;
      else rest += w;
    }
    if (onRoot === 0) continue;
    if (rest > 0) {
      for (let k = 0; k < 4; k++) {
        const w = weight.getComponent(i, k);
        weight.setComponent(i, k, roots.has(index.getComponent(i, k)) ? 0 : w / rest);
      }
    } else {
      v.set(position.getX(i), position.getY(i), position.getZ(i)).applyMatrix4(bind);
      let best = -1;
      let bestD = Infinity;
      restAt.forEach((p, b) => {
        if (p === null) return;
        const d = p.distanceToSquared(v);
        if (d < bestD) {
          bestD = d;
          best = b;
        }
      });
      if (best < 0) continue;
      index.setComponent(i, 0, best);
      weight.setComponent(i, 0, 1);
      for (let k = 1; k < 4; k++) weight.setComponent(i, k, 0);
      index.needsUpdate = true;
    }
    changed++;
  }
  weight.needsUpdate = true;
  return changed;
}
